import { useState, useEffect, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, useAuth } from '@halo/web-lib';
import { Mail, X } from 'lucide-react';

const RESEND_COOLDOWN_SECONDS = 60;

export function EmailVerificationBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const resendMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/api/v1/auth/send-verification');
      if (!res.success) {
        throw new Error(res.error ?? 'Failed to send verification email');
      }
    },
    onSuccess: () => {
      setCooldown(RESEND_COOLDOWN_SECONDS);
    },
  });

  const handleResend = useCallback(() => {
    if (resendMutation.isPending || cooldown > 0) return;
    resendMutation.mutate();
  }, [resendMutation, cooldown]);

  // Don't render if user is verified or banner is dismissed
  if (user?.emailVerified !== false || dismissed) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-[13px]">
      <Mail className="w-4 h-4 shrink-0" />
      <span className="flex-1">
        Please verify your email address. Check your inbox or{' '}
        <button
          type="button"
          onClick={handleResend}
          disabled={resendMutation.isPending || cooldown > 0}
          className="font-medium underline underline-offset-2 hover:text-amber-900 dark:hover:text-amber-100 transition-colors disabled:opacity-50 disabled:no-underline"
        >
          {resendMutation.isPending
            ? 'sending...'
            : cooldown > 0
              ? `resend in ${cooldown}s`
              : 'click here to resend'}
        </button>
        .
        {resendMutation.isSuccess && cooldown > 0 && (
          <span className="ml-2 text-green-700 dark:text-green-400 font-medium">
            Verification email sent!
          </span>
        )}
        {resendMutation.error && (
          <span className="ml-2 text-red-700 dark:text-red-400 font-medium">
            {resendMutation.error.message}
          </span>
        )}
      </span>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="shrink-0 p-0.5 rounded hover:bg-amber-200/50 dark:hover:bg-amber-800/50 transition-colors"
        title="Dismiss for this session"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
